/**
 * Utility functions for number formatting
 * Uses Italian format: dot (.) for thousands separator, comma (,) for decimal separator
 */

/**
 * Format currency/price values with 4 decimal places
 * @param value - The numeric value to format
 * @returns Formatted string with Italian number format (e.g., "1.234,5678")
 */
export const formatCurrency = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === '') {
    return '0,0000';
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    return '0,0000';
  }

  // Format with 4 decimal places
  const formatted = numValue.toFixed(4);

  // Split into integer and decimal parts
  const [integerPart, decimalPart] = formatted.split('.');

  // Add thousand separators to integer part
  const integerFormatted = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  // Combine with comma as decimal separator
  return `${integerFormatted},${decimalPart}`;
};

/**
 * Format quantity values (no decimal places)
 * @param value - The numeric value to format
 * @returns Formatted string with Italian number format (e.g., "1.234")
 */
export const formatQuantity = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === '') {
    return '0';
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    return '0';
  }

  // Round to integer
  const intValue = Math.round(numValue);

  // Add thousand separators
  return intValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

/**
 * Format percentage values with 2 decimal places
 * @param value - The numeric value to format
 * @returns Formatted string with Italian number format (e.g., "12,34%")
 */
export const formatPercentage = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === '') {
    return '0,00%';
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    return '0,00%';
  }

  // Format with 2 decimal places
  const formatted = numValue.toFixed(2);

  // Split into integer and decimal parts
  const [integerPart, decimalPart] = formatted.split('.');

  // Add thousand separators to integer part
  const integerFormatted = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  // Combine with comma as decimal separator
  return `${integerFormatted},${decimalPart}%`;
};

/**
 * Parse Italian formatted number back to float
 * @param value - The formatted string to parse
 * @returns Numeric value
 */
export const parseFormattedNumber = (value: string | null | undefined): number => {
  if (!value) return 0;

  // Remove thousand separators (dots) and replace comma with dot for decimal
  const cleaned = value
    .replace(/\./g, '') // Remove thousand separators
    .replace(',', '.') // Replace decimal comma with dot
    .replace('%', ''); // Remove percentage sign if present

  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Format for Ant Design InputNumber component
 * The parser and formatter for Italian number format
 */
export const inputNumberFormatter = (value: number | undefined): string => {
  if (!value) return '';
  return formatCurrency(value);
};

export const inputNumberParser = (value: string | undefined): number => {
  if (!value) return 0;
  return parseFormattedNumber(value);
};

/**
 * Format for Ant Design InputNumber component (quantities only)
 */
export const quantityFormatter = (value: number | undefined): string => {
  if (!value) return '';
  return formatQuantity(value);
};

export const quantityParser = (value: string | undefined): number => {
  if (!value) return 0;
  return parseFormattedNumber(value);
};